import React, { useEffect, useRef, useState } from 'react';
import { Upload, Download, Trash2, FileText, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../api/axios';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const canPreview = (d) => d.content_type === 'application/pdf' || d.content_type?.startsWith('image/');

const MAX = 25 * 1024 * 1024;
const CHUNK = 4 * 1024 * 1024;
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp';
const errMsg = (e, fallback) => e.response?.data?.detail || fallback;
const sizeLabel = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);

const DocumentosSection = ({ sampleId }) => {
  const [docs, setDocs] = useState([]);
  const [progress, setProgress] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const inputRef = useRef(null);
  const base = `/work-samples/${sampleId}/documentos`;

  const load = () => axios.get(base).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { if (sampleId) load(); }, [sampleId]); // eslint-disable-line

  const upload = async (file) => {
    if (file.size > MAX) return toast.error('El tamaño máximo es 25 MB');
    const uploadId = crypto.randomUUID().replace(/-/g, '');
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const form = new FormData();
        form.append('upload_id', uploadId);
        form.append('index', i);
        form.append('total', total);
        form.append('filename', file.name);
        form.append('file', file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
        await axios.post(`${base}/chunk`, form);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      toast.success('Documento subido');
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Error al subir el documento'));
    } finally {
      setProgress(null);
    }
  };

  const download = async (doc) => {
    try {
      const r = await axios.get(`${base}/${doc.id}/download`);
      window.open(r.data.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(errMsg(e, 'Error al descargar'));
    }
  };

  const preview = async (doc) => {
    try {
      const r = await axios.get(`${base}/${doc.id}/download`, { params: { inline: true } });
      setPreviewDoc({ ...doc, url: r.data.url });
    } catch (e) {
      toast.error(errMsg(e, 'Error al abrir la vista previa'));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`¿Eliminar "${doc.filename}"?`)) return;
    try {
      await axios.delete(`${base}/${doc.id}`);
      toast.success('Documento eliminado');
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Error al eliminar'));
    }
  };

  return (
    <div className="space-y-3 p-4 border rounded-lg bg-gray-50" data-testid="documentos-section">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-gray-700">Documentos</h4>
        {sampleId && (
          <>
            <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" data-testid="documento-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f); }} />
            <Button type="button" size="sm" variant="outline" disabled={progress !== null} onClick={() => inputRef.current?.click()} data-testid="upload-documento-btn">
              {progress !== null ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />} Subir documento
            </Button>
          </>
        )}
      </div>
      {!sampleId && <p className="text-sm text-gray-500" data-testid="documentos-save-first">Guarda la muestra para poder adjuntar documentos.</p>}
      {progress !== null && <Progress value={progress} data-testid="documento-upload-progress" />}
      {sampleId && docs.length === 0 && progress === null && <p className="text-sm text-gray-500">Sin documentos. PDF, Word, Excel o imágenes, hasta 25 MB.</p>}
      {docs.map((d) => (
        <div key={d.id} className="flex items-center gap-2 bg-white border rounded-md px-3 py-2 text-sm" data-testid={`documento-${d.id}`}>
          <FileText size={16} className="text-gray-500 shrink-0" />
          <span className="flex-1 truncate" title={d.filename}>{d.filename}</span>
          <span className="text-xs text-gray-500 whitespace-nowrap">{sizeLabel(d.size)}</span>
          {canPreview(d) && (
            <Button type="button" size="sm" variant="ghost" onClick={() => preview(d)} data-testid={`preview-documento-${d.id}`}><Eye size={14} /></Button>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={() => download(d)} data-testid={`download-documento-${d.id}`}><Download size={14} /></Button>
          <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => remove(d)} data-testid={`delete-documento-${d.id}`}><Trash2 size={14} /></Button>
        </div>
      ))}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && setPreviewDoc(null)}>
        <DialogContent className="max-w-5xl w-[95vw]" data-testid="documento-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{previewDoc?.filename}</DialogTitle>
          </DialogHeader>
          <div className="h-[75vh] bg-gray-100 rounded-md overflow-auto flex items-center justify-center">
            {previewDoc && (previewDoc.content_type === 'application/pdf' ? (
              <iframe src={previewDoc.url} title={previewDoc.filename} className="w-full h-full border-0" data-testid="documento-preview-pdf" />
            ) : (
              <img src={previewDoc.url} alt={previewDoc.filename} className="max-w-full max-h-full object-contain" data-testid="documento-preview-img" />
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => download(previewDoc)} data-testid="preview-download-btn"><Download size={14} className="mr-1" /> Descargar</Button>
            <Button type="button" onClick={() => setPreviewDoc(null)} data-testid="preview-close-btn">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentosSection;
